/**
 * 装配节点路径工具。
 *
 * 文件职责：
 * 1. 统一 assemblyNodeId 的路径规范化规则；
 * 2. 为 Mate 归属、设计树投影和持久化恢复提供可复用纯函数；
 * 3. 不依赖 Viewer、Object3D 或 UI 状态。
 */

/**
 * 规范化装配节点 ID。
 *
 * @param assemblyNodeId 原始装配节点 ID。
 * @returns 去除首尾分隔符并统一为 `/` 的节点 ID。
 */
export function normalizeAssemblyNodeId(assemblyNodeId: string): string {
  return assemblyNodeId.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
}

/**
 * 归一化可能不是字符串的装配节点 ID。
 *
 * @param value 待归一化值。
 * @returns 可用于匹配的装配节点 ID；非法值返回空字符串。
 */
export function normalizeOptionalAssemblyNodeId(value: unknown): string {
  return typeof value === 'string' ? normalizeAssemblyNodeId(value).trim() : '';
}

/**
 * 安全解码装配节点 ID。
 *
 * @param assemblyNodeId 原始装配节点 ID。
 * @returns 可解码时返回解码后的规范 ID；无法解码时返回原规范 ID。
 */
export function decodeAssemblyNodeIdSafely(assemblyNodeId: string): string {
  try {
    // 离线包历史快照会把 #、: 等路径字符 URL 编码，比较稳定身份时必须回到语义 ID。
    return normalizeAssemblyNodeId(decodeURIComponent(assemblyNodeId));
  } catch {
    return normalizeAssemblyNodeId(assemblyNodeId);
  }
}

/**
 * 收集装配节点身份匹配用的 lookup ID。
 *
 * 设计说明：
 * 1. 项目模型、离线包 manifest 与历史快照可能混用编码/未编码的节点 ID；
 * 2. 运行时 Object3D uuid、selectionObjectId、assemblyNodeId 都需要进入同一套比较规则；
 * 3. 返回值保持去重，调用方可直接构造 Set 进行 O(1) 命中。
 *
 * @param value 待归一化的节点身份值。
 * @returns 可用于稳定身份匹配的 lookup ID 列表。
 */
export function collectAssemblyNodeIdentityLookupIds(value: unknown): string[] {
  const normalized = normalizeOptionalAssemblyNodeId(value);
  if (!normalized) {
    return [];
  }

  const decoded = decodeAssemblyNodeIdSafely(normalized);
  return [
    normalized,
    decoded,
    normalizeAssemblyNodeLookupId(normalized),
    normalizeAssemblyNodeLookupId(decoded),
  ]
    .filter((item) => item.length > 0)
    .filter((item, index, array) => array.indexOf(item) === index);
}

/**
 * 收集装配节点路径的尾部身份 lookup ID。
 *
 * 设计说明：
 * 子装配离线包恢复到父工作区时，Mate 文档会把局部节点锚定到父路径；
 * 但运行时 Object3D metadata 可能仍只携带离线包内部的局部节点 ID。
 * 尾部 lookup 只作为精确身份失败后的兼容判断，避免把路径拼接规则散落到高亮或求解代码中。
 *
 * @param value 待归一化的节点身份值。
 * @returns 从完整路径逐段截取的尾部 lookup ID 列表。
 */
function collectAssemblyNodeIdentitySuffixLookupIds(value: unknown): string[] {
  const lookupIds = collectAssemblyNodeIdentityLookupIds(value);
  return lookupIds
    .flatMap((lookupId) => {
      const segments = splitAssemblyNodePath(lookupId);
      return segments.map((_, index) => (
        normalizeAssemblyNodeLookupId(segments.slice(index).join('/'))
      ));
    })
    .filter((item) => item.length > 0)
    .filter((item, index, array) => array.indexOf(item) === index);
}

/**
 * 判断两个装配节点身份是否等价。
 *
 * 设计说明：
 * 1. 离线包、历史快照与父工作区运行时 metadata 可能分别保存 URL 编码或未编码的 assemblyNodeId；
 * 2. 子装配切换后，Mate 文档中的实体可能已经拼上父装配路径，而 imported mesh 仍只知道局部实体 ID；
 * 3. 等价判断必须集中在装配节点路径工具中，避免 Mate 高亮、求解和模型加载链路各自复制字符串兼容逻辑。
 *
 * @param left 左侧装配节点身份。
 * @param right 右侧装配节点身份。
 * @returns 两侧身份在归一化 lookup 集合中存在交集时返回 true。
 */
export function areAssemblyNodeIdentityEquivalent(left: unknown, right: unknown): boolean {
  const leftLookupIds = collectAssemblyNodeIdentityLookupIds(left);
  if (leftLookupIds.length === 0) {
    return false;
  }

  const rightLookupIdSet = new Set(collectAssemblyNodeIdentityLookupIds(right));
  if (rightLookupIdSet.size === 0) {
    return false;
  }

  if (leftLookupIds.some((lookupId) => rightLookupIdSet.has(lookupId))) {
    return true;
  }

  const rightSuffixLookupIdSet = new Set(collectAssemblyNodeIdentitySuffixLookupIds(right));
  if (rightSuffixLookupIdSet.size === 0) {
    return false;
  }

  return collectAssemblyNodeIdentitySuffixLookupIds(left)
    .some((lookupId) => rightSuffixLookupIdSet.has(lookupId));
}

/**
 * 归一化装配节点模糊匹配 ID。
 *
 * 设计说明：
 * 设计树与 imported-model 选择快照可能一个携带 `Part1[0]`，另一个只携带 `Part1`。
 * 模糊匹配必须统一在 cad-core，避免 web-client 为 Mate 单独复制路径规则。
 *
 * @param assemblyNodeId 原始装配节点 ID。
 * @returns 去除实例索引后的装配节点路径。
 */
export function normalizeAssemblyNodeLookupId(assemblyNodeId: string): string {
  return splitAssemblyNodePath(assemblyNodeId)
    .map((segment) => segment.replace(/\[\d+\]$/u, '').trim())
    .filter((segment) => segment.length > 0)
    .join('/');
}

/**
 * 拆分装配节点路径。
 *
 * @param assemblyNodeId 装配节点 ID。
 * @returns 已去除空片段的路径片段。
 */
export function splitAssemblyNodePath(assemblyNodeId: string): string[] {
  return normalizeAssemblyNodeId(assemblyNodeId)
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

/**
 * 解析装配节点的父级路径。
 *
 * @param assemblyNodeId 装配节点 ID。
 * @returns 父级路径片段；顶层或无效节点返回空数组。
 */
export function resolveAssemblyNodeParentPath(assemblyNodeId: string): string[] {
  return splitAssemblyNodePath(assemblyNodeId).slice(0, -1);
}

/**
 * 解析多个装配节点的最低公共父装配体。
 *
 * 设计说明：
 * SOLIDWORKS 将跨组件 Mate 放在同时包含参与组件的最低层装配体中。
 * 因此这里比较的是“组件节点的父路径”，而不是组件节点本身。
 *
 * @param input 解析输入。
 * @returns 最低公共父装配体节点 ID；无法解析时回退到文档装配体 ID。
 */
export function resolveLowestCommonParentAssemblyNodeId(input: {
  /** 当前 Mate 文档所属装配体 ID。 */
  readonly documentAssemblyId: string;
  /** 参与配合的组件或基准驱动组件节点 ID。 */
  readonly assemblyNodeIds: readonly string[];
}): string {
  const parentPaths = input.assemblyNodeIds
    .map(resolveAssemblyNodeParentPath)
    .filter((path) => path.length > 0);

  if (parentPaths.length === 0) {
    return normalizeAssemblyNodeId(input.documentAssemblyId);
  }

  const commonPath: string[] = [];
  const commonLength = Math.min(...parentPaths.map((path) => path.length));
  for (let index = 0; index < commonLength; index += 1) {
    const candidate = parentPaths[0]?.[index];
    if (!candidate || parentPaths.some((path) => path[index] !== candidate)) {
      break;
    }
    commonPath.push(candidate);
  }

  return commonPath.length > 0
    ? commonPath.join('/')
    : normalizeAssemblyNodeId(input.documentAssemblyId);
}
